import { useEffect, useMemo, useState } from "react";

import { competitorFxRates } from "../../../services/remittance";
import styles from "../styles/GetAppLanding.module.scss";
import { competitorRows } from "./landingData";
import usFlag from "../assets/us-flag.svg";
import { formatInr, formatRate, formatUsd, toNumber } from "./rateUtils";

const getRecipientAmount = (row, amount) => {
  const sentAfterFee = Math.max(amount - row.fee, 0);
  return sentAfterFee * row.rate;
};

const formatTableInr = (value) => formatInr(Math.round(toNumber(value)));

const formatTableFee = (value) =>
  toNumber(value) ? `$${toNumber(value).toFixed(2)}` : "$0.00";

const DifferencePill = ({ value, positive = false }) => (
  <span
    className={`${positive ? styles.positivePill : styles.negativePill} ${
      positive ? styles.extraPill : ""
    }`}
  >
    {positive ? <span aria-hidden="true" /> : null}
    {positive
      ? `${formatTableInr(Math.abs(value))} EXTRA`
      : `- ${formatTableInr(Math.abs(value))}`}
  </span>
);

const normalizeProvider = (provider) => ({
  name: provider.provider_name || provider.name,
  logo:
    provider.logo ||
    provider.light_logo_url ||
    provider.dark_logo_url ||
    provider.logoUrl,
  rate: toNumber(provider.fx_rate || provider.rate),
  fee: toNumber(provider.transfer_fee || provider.fee),
});

const DataCell = ({ label, children, highlight = false, className = "" }) => (
  <div className={`${styles.compareMetric} ${className}`}>
    <span>{label}</span>
    <strong className={highlight ? styles.compareHighlight : ""}>
      {children}
    </strong>
  </div>
);

const RateComparison = ({ rateModel }) => {
  const [apiCompetitors, setApiCompetitors] = useState([]);
  const [showAllMobile, setShowAllMobile] = useState(false);
  const aboundRecipient = rateModel.recipientAmount;
  const competitorAmounts = useMemo(() => {
    const competitorSource = apiCompetitors.length
      ? apiCompetitors
      : apiCompetitors;

    return competitorSource
      .map(normalizeProvider)
      .filter(
        (row) => row.name && row.rate > 0 && row.rate <= rateModel.blendedRate,
      )
      .map((row) => ({
        ...row,
        recipient: getRecipientAmount(row, rateModel.amount),
      }));
  }, [apiCompetitors, rateModel.amount, rateModel.blendedRate]);
  const bestCompetitorAmount = Math.max(
    ...competitorAmounts.map((row) => row.recipient),
    0,
  );
  const mobileRowsClass = showAllMobile
    ? styles.compareRows
    : `${styles.compareRows} ${styles.compareRowsCollapsed}`;

  useEffect(() => {
    const controller = new AbortController();

    competitorFxRates({ signal: controller.signal })
      .then((providers) => {
        setApiCompetitors(providers);
      })
      .catch((error) => {
        if (error?.name !== "CanceledError") {
          console.log(error);
        }
      });

    return () => controller.abort();
  }, []);

  return (
    <section className={styles.rateSection} id="rate-comparison">
      <div className={styles.sectionIntro}>
        <span className={styles.sectionBadge}>
          <i className={styles.liveRateDot} aria-hidden="true" />
          Live Rate Comparison
        </span>
        <h2>Send more with every dollar</h2>
        <p>No hidden fees & best exchange rates. Always.</p>
      </div>
      <div className={styles.comparePanel}>
        <div className={styles.sendingControl}>
          <span>You’re Sending</span>
          <strong>
            {formatUsd(rateModel.amount)}
            <img src={usFlag} alt="" aria-hidden="true" />
          </strong>
        </div>
        <div className={styles.compareHeader}>
          <span>Provider</span>
          <span>Exchange Rate</span>
          <span>Fee</span>
          <span>Recipient Gets</span>
          <span>Difference</span>
        </div>
        <div className={`${styles.compareRow} ${styles.aboundRow}`}>
          <div className={styles.providerName}>Abound</div>
          <DataCell label="Exchange Rate" className={styles.rateCell}>
            {formatRate(rateModel.blendedRate)}
          </DataCell>
          <DataCell label="Fee" className={styles.feeCell} highlight>
            $0.00
          </DataCell>
          <div className={styles.aboundRecipient}>
            <DataCell
              label="Recipient Gets"
              className={styles.recipientCell}
              highlight
            >
              {formatTableInr(aboundRecipient)}
            </DataCell>
            <a href="https://accounts.joinabound.com/getapp/signup/v2?source=remittance">Send now</a>
          </div>
          <DataCell
            label="Difference"
            className={`${styles.differenceCell} ${styles.aboundExtra}`}
          >
            <DifferencePill
              value={Math.max(aboundRecipient - bestCompetitorAmount, 0)}
              positive
            />
          </DataCell>
        </div>
        <div className={mobileRowsClass}>
          {competitorAmounts.map((row) => {
            return (
              <div className={styles.compareRow} key={row.name}>
                <div className={styles.providerLogo}>
                  <img src={row.logo} alt={row.name} />
                </div>
                <DataCell label="Exchange Rate" className={styles.rateCell}>
                  {formatRate(row.rate)}
                </DataCell>
                <DataCell label="Fee" className={styles.feeCell}>
                  {formatTableFee(row.fee)}
                </DataCell>
                <DataCell
                  label="Recipient Gets"
                  className={styles.recipientCell}
                >
                  {formatTableInr(row.recipient)}
                </DataCell>
                <DataCell label="Difference" className={styles.differenceCell}>
                  <DifferencePill
                    value={Math.max(aboundRecipient - row.recipient, 0)}
                  />
                </DataCell>
              </div>
            );
          })}
        </div>
        {competitorAmounts.length > 2 ? (
          <button
            className={styles.seeMoreButton}
            type="button"
            onClick={() => setShowAllMobile((value) => !value)}
          >
            {showAllMobile ? "See less" : "See more"}
            <span className={showAllMobile ? styles.chevronOpen : ""}>⌄</span>
          </button>
        ) : null}
      </div>
    </section>
  );
};

export default RateComparison;
